<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import UserLoginLogsAPI from 'dashboard/api/userLoginLogs';

const store = useStore();
const { t } = useI18n();

const agents = useMapGetter('agents/getAgents');
const globalConfig = useMapGetter('globalConfig/get');

const logs = ref([]);
const meta = ref({ total_count: 0, current_page: 1, total_pages: 1 });
const isLoading = ref(false);
const isExporting = ref(false);
const isGeneratingPdf = ref(false);

const filters = ref({ since: null, until: null, userId: null });
const currentPage = ref(1);

const agentOptions = computed(() => [
  { id: null, name: t('USER_LOGIN_REPORT.FILTERS.ALL_AGENTS') },
  ...agents.value,
]);

const fetchLogs = async (page = 1) => {
  isLoading.value = true;
  try {
    const { data } = await UserLoginLogsAPI.get({
      page,
      since: filters.value.since,
      until: filters.value.until,
      userId: filters.value.userId,
    });
    logs.value = data.data;
    meta.value = data.meta;
    currentPage.value = page;
  } catch {
    useAlert(t('USER_LOGIN_REPORT.ERRORS.LOAD_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

const onDateRangeChange = ([start, end]) => {
  filters.value.since = start ? Math.floor(new Date(start).getTime() / 1000) : null;
  filters.value.until = end ? Math.floor(new Date(end).getTime() / 1000) : null;
  fetchLogs(1);
};

const onFilterChange = () => fetchLogs(1);

const exportCSV = async () => {
  isExporting.value = true;
  try {
    const { data } = await UserLoginLogsAPI.download({
      since: filters.value.since,
      until: filters.value.until,
      userId: filters.value.userId,
    });
    downloadCsvFile('relatorio_login_usuarios.csv', data);
  } catch {
    useAlert(t('USER_LOGIN_REPORT.ERRORS.DOWNLOAD_FAILED'));
  } finally {
    isExporting.value = false;
  }
};

const formatDateTime = iso => {
  if (!iso) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'America/Sao_Paulo',
  }).format(new Date(iso));
};

const CLOSE_SCRIPT = '\x3C/script>';

const fmtTs = ts =>
  ts
    ? new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'America/Sao_Paulo',
      }).format(new Date(ts * 1000))
    : null;

const exportPDF = async () => {
  isGeneratingPdf.value = true;
  try {
    const logoPath = globalConfig.value?.logoThumbnail;
    let logoHtml = `<span style="font-size:20px;font-weight:700;color:#fff;letter-spacing:-0.5px">${globalConfig.value?.name || 'TalkChat'}</span>`;
    if (logoPath) {
      try {
        const res = await fetch(`${window.location.origin}${logoPath}`);
        const blob = await res.blob();
        const b64 = await new Promise(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        logoHtml = `<img src="${b64}" style="height:40px;max-width:160px;object-fit:contain;filter:brightness(0) invert(1)" alt="logo">`;
      } catch { /* fallback to text */ }
    }

    const companyName = globalConfig.value?.name || 'TalkChat';
    const since = fmtTs(filters.value.since);
    const until = fmtTs(filters.value.until);
    const periodLabel = since && until
      ? `${since} até ${until}`
      : since ? `A partir de ${since}` : 'Todos os períodos';

    const selectedAgent = filters.value.userId
      ? agents.value.find(a => a.id === filters.value.userId)?.name || 'Desconhecido'
      : 'Todos os agentes';

    const now = new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo',
    }).format(new Date());

    const rows = logs.value.map(log => `<tr>
      <td style="padding:9px 14px;border-bottom:1px solid #f1f5f9;color:#1e293b;font-weight:500">${log.user_name}</td>
      <td style="padding:9px 14px;border-bottom:1px solid #f1f5f9;color:#475569;font-variant-numeric:tabular-nums">${formatDateTime(log.logged_in_at)}</td>
      <td style="padding:9px 14px;border-bottom:1px solid #f1f5f9;color:#475569;font-variant-numeric:tabular-nums">${formatDateTime(log.logged_out_at)}</td>
      <td style="padding:9px 14px;border-bottom:1px solid #f1f5f9;color:#475569">${log.duration || '-'}</td>
    </tr>`).join('');

    const emptyRow = `<tr><td colspan="4" style="text-align:center;padding:40px;color:#94a3b8;font-size:13px">Nenhum registro encontrado para os filtros selecionados.</td></tr>`;

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8">
<title>Relatório de Login de Usuários</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,sans-serif;font-size:12px;color:#1e293b;background:#fff}
.header{background:#00B5A9;padding:20px 28px;display:flex;align-items:center;gap:16px}
.header-text h1{color:#fff;font-size:17px;font-weight:700}
.header-text p{color:rgba(255,255,255,.75);font-size:11px;margin-top:3px}
.meta{padding:14px 28px;background:#f8fafc;border-bottom:1px solid #e2e8f0;display:flex;gap:28px;flex-wrap:wrap}
.meta-item{font-size:11px;color:#64748b}
.meta-item strong{color:#1e293b;display:block;font-size:12px;margin-bottom:1px}
.content{padding:20px 28px}
table{width:100%;border-collapse:collapse}
thead tr{background:#f8fafc;border-top:2px solid #00B5A9}
th{padding:10px 14px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#64748b}
tbody tr:last-child td{border-bottom:none}
.footer{margin:0 28px 20px;padding-top:12px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:10px;color:#94a3b8}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style>
</head>
<body>
<div class="header">
  ${logoHtml}
  <div class="header-text">
    <h1>Relatório de Login de Usuários</h1>
    <p>${companyName}</p>
  </div>
</div>
<div class="meta">
  <div class="meta-item"><strong>Período</strong>${periodLabel}</div>
  <div class="meta-item"><strong>Agente</strong>${selectedAgent}</div>
  <div class="meta-item"><strong>Registros</strong>${logs.value.length} (página atual)</div>
</div>
<div class="content">
  <table>
    <thead><tr><th>Usuário</th><th>Login</th><th>Logout</th><th>Tempo Total</th></tr></thead>
    <tbody>${rows || emptyRow}</tbody>
  </table>
</div>
<div class="footer">
  <span>Gerado em ${now}</span>
  <span>${companyName} · TalkChat</span>
</div>
<script>window.onload=function(){window.print()}${CLOSE_SCRIPT}
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    window.open(URL.createObjectURL(blob), '_blank');
  } finally {
    isGeneratingPdf.value = false;
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  fetchLogs();
});
</script>

<template>
  <div class="flex flex-col gap-6 print:gap-4">
    <div class="flex items-center justify-between print:hidden">
      <h2 class="text-xl font-semibold text-n-slate-12">
        {{ $t('USER_LOGIN_REPORT.TITLE') }}
      </h2>
      <div class="flex gap-2">
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-colors disabled:opacity-50"
          :disabled="isLoading"
          @click="fetchLogs(currentPage)"
        >
          <span
            :class="isLoading ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-refresh-cw'"
            class="size-4"
          />
        </button>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-colors disabled:opacity-50"
          :disabled="isExporting"
          @click="exportCSV"
        >
          <span class="i-lucide-file-spreadsheet size-4" />
          {{ isExporting ? $t('USER_LOGIN_REPORT.EXPORTING') : $t('USER_LOGIN_REPORT.EXPORT_CSV') }}
        </button>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-colors disabled:opacity-50"
          :disabled="isGeneratingPdf"
          @click="exportPDF"
        >
          <span
            :class="isGeneratingPdf ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-file-text'"
            class="size-4"
          />
          {{ isGeneratingPdf ? $t('USER_LOGIN_REPORT.EXPORTING') : $t('USER_LOGIN_REPORT.EXPORT_PDF') }}
        </button>
      </div>
    </div>

    <div class="flex flex-wrap gap-3 print:hidden">
      <WootDatePicker @date-range-changed="onDateRangeChange" />

      <select
        v-model="filters.userId"
        class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-teal-8"
        @change="onFilterChange"
      >
        <option
          v-for="agent in agentOptions"
          :key="String(agent.id)"
          :value="agent.id"
        >
          {{ agent.name }}
        </option>
      </select>
    </div>

    <div class="overflow-x-auto rounded-xl border border-n-weak">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-n-weak bg-n-surface-2">
            <th class="px-4 py-3 text-left font-medium text-n-slate-11">
              {{ $t('USER_LOGIN_REPORT.TABLE.USER') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11">
              {{ $t('USER_LOGIN_REPORT.TABLE.LOGIN') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11">
              {{ $t('USER_LOGIN_REPORT.TABLE.LOGOUT') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11">
              {{ $t('USER_LOGIN_REPORT.TABLE.DURATION') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading">
            <td colspan="4" class="px-4 py-8 text-center text-n-slate-11">
              <span class="i-lucide-loader-2 animate-spin inline-block size-5" />
            </td>
          </tr>
          <tr v-else-if="logs.length === 0">
            <td colspan="4" class="px-4 py-8 text-center text-n-slate-11">
              {{ $t('USER_LOGIN_REPORT.NO_DATA') }}
            </td>
          </tr>
          <tr
            v-for="log in logs"
            :key="log.id"
            class="border-b border-n-weak last:border-0 hover:bg-n-alpha-1"
          >
            <td class="px-4 py-3 font-medium text-n-slate-12">{{ log.user_name }}</td>
            <td class="px-4 py-3 text-n-slate-11 tabular-nums">{{ formatDateTime(log.logged_in_at) }}</td>
            <td class="px-4 py-3 text-n-slate-11 tabular-nums">{{ formatDateTime(log.logged_out_at) }}</td>
            <td class="px-4 py-3 text-n-slate-11">{{ log.duration || '-' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="meta.total_pages > 1"
      class="flex items-center justify-between print:hidden"
    >
      <span class="text-sm text-n-slate-11">
        {{ $t('USER_LOGIN_REPORT.PAGINATION', { total: meta.total_count }) }}
      </span>
      <div class="flex gap-1">
        <button
          v-for="page in meta.total_pages"
          :key="page"
          class="px-3 py-1 text-sm rounded-lg border border-n-weak transition-colors"
          :class="page === currentPage ? 'bg-n-teal-9 text-white border-n-teal-9' : 'text-n-slate-11 hover:bg-n-alpha-1'"
          @click="fetchLogs(page)"
        >
          {{ page }}
        </button>
      </div>
    </div>
  </div>
</template>
